import { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { UserPlus } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { useSearchClients } from '@/api/hooks';

interface ClientAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onSelect: (clientId: string, clientName: string) => void;
  onCreateNew: (name: string) => void;
  onViewClient: () => void;
  linkedClientId: string | null;
  getAccessToken: () => Promise<string>;
}

export function ClientAutocomplete({
  value,
  onChange,
  onSelect,
  onCreateNew,
  onViewClient,
  linkedClientId,
  getAccessToken,
}: Readonly<ClientAutocompleteProps>) {
  const { t } = useTranslation();
  const [showDropdown, setShowDropdown] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const containerRef = useRef<HTMLDivElement>(null);

  const { data: searchResults } = useSearchClients(getAccessToken, debouncedSearch, showDropdown);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (value.length >= 2 && !linkedClientId) {
      debounceRef.current = setTimeout(() => {
        setDebouncedSearch(value);
        setShowDropdown(true);
      }, 300);
    } else {
      setShowDropdown(false);
    }
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [value, linkedClientId]);

  const handleSelect = useCallback(
    (clientId: string, clientName: string) => {
      setShowDropdown(false);
      onSelect(clientId, clientName);
    },
    [onSelect],
  );

  if (linkedClientId) {
    return (
      <div className="flex items-center gap-2" data-testid="client-linked-badge">
        <Badge
          variant="secondary"
          className="cursor-pointer"
          onClick={onViewClient}
          data-testid="client-badge"
        >
          {value}
        </Badge>
      </div>
    );
  }

  return (
    <div className="relative" ref={containerRef}>
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={t('clients.search')}
        data-testid="client-autocomplete-input"
        onFocus={() => {
          if (value.length >= 2) {
            setDebouncedSearch(value);
            setShowDropdown(true);
          }
        }}
        onBlur={() => {
          setTimeout(() => setShowDropdown(false), 200);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setShowDropdown(false);
        }}
      />
      {showDropdown && (
        <div
          className="bg-popover border-border absolute z-50 mt-1 w-full rounded-md border shadow-md"
          data-testid="client-dropdown"
        >
          {searchResults?.clients.map((client) => (
            <button
              key={client.id}
              type="button"
              className="hover:bg-accent flex w-full items-center justify-between px-3 py-2 text-left text-sm"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => handleSelect(client.id, client.name)}
              data-testid={`client-option-${client.id}`}
            >
              <span>{client.name}</span>
              <span className="text-muted-foreground text-xs">
                {client.phone || client.email || ''}
              </span>
            </button>
          ))}
          <button
            type="button"
            className="hover:bg-accent flex w-full items-center gap-2 border-t px-3 py-2 text-left text-sm"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              setShowDropdown(false);
              onCreateNew(value);
            }}
            data-testid="client-create-new"
          >
            <UserPlus className="h-4 w-4" />
            {t('clients.saveNew')}
          </button>
        </div>
      )}
    </div>
  );
}
